import { useQuery } from '@tanstack/react-query';

import { businessUnitQueryKeys } from '@xemplo/gp-constants';
import {
  BusinessUnit,
  ListPagedResult,
  PagedRequest,
  Result,
  ResultV2,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { BusinessUnitAPI } from './business-unit-query.constants';
import { mapIntegrationSettings } from './business-unit-query.helpers';
import { BusinessUnitIntegrationSetting } from './business-unit-query.types';

export const useGetBusinessUnitsQuery = (props: { requestParams?: PagedRequest }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams } = props;
  const url = apiUrlBuilder(
    BusinessUnitAPI.businessUnits,
    applicationApiUrl,
    requestParams,
  );

  return useQuery<ListPagedResult<BusinessUnit>>({
    queryKey: businessUnitQueryKeys.list(url),
    queryFn: () =>
      client.get<ListPagedResult<BusinessUnit>>(url).then((resp) => resp.data),
    enabled: !!applicationApiUrl,
  });
};

export const useGetBusinessUnitByIdQuery = (props: { id?: string | number | null }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id } = props;

  const url = apiUrlBuilder(BusinessUnitAPI.businessUnitById(id), applicationApiUrl);

  return useQuery({
    queryKey: businessUnitQueryKeys.detail(String(id), url),
    queryFn: () => client.get<Result<BusinessUnit>>(url).then((resp) => resp.data),
    enabled: !!(id && applicationApiUrl),
  });
};

/**
 * Fetches the complete set of integration settings for a business unit. Exposes the
 * raw GET rows (the write source of truth for the full-replace PUT) alongside a derived
 * boolean-per-setting Record for display and form defaults. See docs/adr/0003.
 */
export const useGetIntegrationSettingsQuery = (props: {
  businessUnitId?: string | null;
}) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { businessUnitId } = props;

  const url = apiUrlBuilder(
    BusinessUnitAPI.integrationSettings(businessUnitId),
    applicationApiUrl,
  );

  return useQuery({
    queryKey: businessUnitQueryKeys.integrationSettings(String(businessUnitId), url),
    queryFn: () =>
      client
        .get<ResultV2<BusinessUnitIntegrationSetting[]>>(url)
        .then((resp) => resp.data),
    select: mapIntegrationSettings,
    enabled: !!(businessUnitId && applicationApiUrl),
  });
};
